import { api } from './api';
import type { Post } from './posts.service';

/** One group of GET /attention: its true size and the first rows of it. */
export interface AttentionGroupData<T> {
  total: number;
  items: T[];
}

export interface AttentionTokenRow {
  /** SCREAMING_CASE platform. */
  platform: string;
  state: 'expired' | 'expiring';
  connectState: string;
  /** Whole days until it lapses, only while expiring (0 = today). */
  daysLeft: number | null;
}

export interface AttentionSlaRow {
  conversationKey: string;
  peer: string;
  levelName: string;
  minutesLate: number;
}

export interface AttentionMediaRow {
  id: string;
  name: string;
  state: 'failed' | 'stuck';
}

export interface AttentionKnowledgeRow {
  key: string;
  conversationKey: string;
  question: string;
  customer: string;
  at: string;
}

/**
 * GET /attention (F19): everything that needs a person, counted on the
 * server, net of the brand's dismissals. The badge, the Attention page and
 * the Overview all read this one answer.
 */
export interface AttentionData {
  total: number;
  risk: AttentionGroupData<Post>;
  failed: AttentionGroupData<Post>;
  /** Posts a channel no longer has; `total` counts channels. */
  gone: AttentionGroupData<Post>;
  tokens: AttentionGroupData<AttentionTokenRow>;
  /** Null on a plan without approvals. */
  approvals: AttentionGroupData<Post> | null;
  sla: AttentionGroupData<AttentionSlaRow>;
  media: AttentionGroupData<AttentionMediaRow>;
  /** Rows only where the plan has the knowledge base; elsewhere a count for one upgrade note. */
  knowledge: AttentionGroupData<AttentionKnowledgeRow> & { allowed: boolean };
  /** Active dismissals: key → ISO time it lapses. */
  dismissed: Record<string, string>;
}

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

function group<T>(raw: unknown): AttentionGroupData<T> {
  const g = (raw && typeof raw === 'object' ? raw : {}) as Partial<AttentionGroupData<T>>;
  return { total: num(g.total), items: Array.isArray(g.items) ? g.items : [] };
}

/** Fills what an older or partial answer leaves out, so a missing group reads as empty, never as a crash. */
export function normalizeAttention(raw: unknown): AttentionData {
  const d = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof AttentionData, unknown>>;
  const knowledge = (d.knowledge && typeof d.knowledge === 'object' ? d.knowledge : {}) as { allowed?: unknown };
  const dismissed = d.dismissed && typeof d.dismissed === 'object' ? (d.dismissed as Record<string, string>) : {};
  return {
    total: num(d.total),
    risk: group<Post>(d.risk),
    failed: group<Post>(d.failed),
    gone: group<Post>(d.gone),
    tokens: group<AttentionTokenRow>(d.tokens),
    approvals: d.approvals ? group<Post>(d.approvals) : null,
    sla: group<AttentionSlaRow>(d.sla),
    media: group<AttentionMediaRow>(d.media),
    knowledge: { ...group<AttentionKnowledgeRow>(d.knowledge), allowed: knowledge.allowed === true },
    dismissed,
  };
}

export const attentionService = {
  async get(): Promise<AttentionData> {
    const res = await api.get<unknown>('/attention');
    return normalizeAttention(res.data);
  },

  /** Quiets a row for the whole brand for 24 hours. */
  async dismiss(key: string): Promise<{ key: string; expiresAt: string }> {
    const res = await api.post<{ key: string; expiresAt: string }>('/attention/dismissals', { key });
    return res.data;
  },

  async restore(key: string): Promise<void> {
    await api.delete(`/attention/dismissals/${encodeURIComponent(key)}`);
  },
};
